import { useCallback, useEffect, useRef, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import useLocalStorage from "../../hooks/useLocalStorage";
import {
  AddPropertyFormTab,
  PropertyCardLayoutTab,
} from "../TabContent";
import "./TabNavigation.css";

const TabNavigation = ({
  showToast,
  setIsLoading,
  pageId = "builder",
}) => {
  // Navigation tabs configuration
  // Single Property Layout tab is commented out for future implementation
  const navigationTabs = [
    { id: "add-property-form", label: __("Add Property Form", "havenlytics"), icon: "plus-square" },
    { id: "property-card-layout", label: __("Property Card Layout", "havenlytics"), icon: "th-large" },
    // { id: "single-property-layout", label: __("Single Property Layout", "havenlytics"), icon: "square" }, // Will be enabled in future version
  ];

  const [activeTab, setActiveTab] = useState(navigationTabs[0].id);
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  
  // State for hover animation
  const [hoverTab, setHoverTab] = useState(null);
  const tabsWrapperRef = useRef(null);

  // Track reset operations to prevent duplicate toasts
  const resetInProgressRef = useRef(false);

  // Separate state for layout tabs
  const [propertyCardLayoutData, setPropertyCardLayoutData] = useState({
    layout: 'grid',
    columns: 3,
    style: 'modern',
    showPrice: true,
    showAddress: true
  });
  
  // State for single property layout (commented out for future use)
  /*
  const [singlePropertyLayoutData, setSinglePropertyLayoutData] = useState({
    sidebar: 'right',
    gallery: true,
    map: true,
    showAmenities: true
  });
  */

  // Store the save/reset functions from each tab
  const [tabActions, setTabActions] = useState({
    "add-property-form": { saveConfiguration: null, resetConfiguration: null },
    "property-card-layout": { saveConfiguration: null, resetConfiguration: null },
    // "single-property-layout": { saveConfiguration: null, resetConfiguration: null } // Reserved for future use
  });

  useLocalStorage(
    `hvnly--:${pageId}:active-tab`,
    activeTab,
    setActiveTab,
    navigationTabs[0].id
  );

  const handleTabClick = useCallback((tabId) => {
    setActiveTab(tabId);
    setHoverTab(null);
  }, []);

  // Sticky header effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsSticky(scrollTop > 5);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ========== SAVE HANDLER ==========
  const handleSave = useCallback(async () => {
    // console.log('💾 SAVE BUTTON CLICKED - Starting save process for:', activeTab);
    setIsSaving(true);
    setIsLoading(true);
    
    try {
      const currentActions = tabActions[activeTab];
      
      if (currentActions?.saveConfiguration) {
        // console.log('🔗 Calling saveConfiguration for current tab...');
        const success = await currentActions.saveConfiguration();
        // console.log('✅ Save operation completed with result:', success);
        
        if (!success) {
          // console.log('❌ Save failed - error toast already shown');
        }
      } else {
        // console.log('ℹ️ No API save operation for this tab, showing success message');
        showToast('success', __('Configuration Saved', 'havenlytics'), `${getTabDisplayName(activeTab)} ` + __('settings saved successfully!', 'havenlytics'));
      }
      
    } catch (error) {
      // console.error('❌ Save operation failed:', error);
      showToast('error', __('Save Failed', 'havenlytics'), __('Failed to save configuration:', 'havenlytics') + ` ${error.message}`);
    } finally {
      setIsSaving(false);
      setIsLoading(false);
      // console.log('🏁 Save process completed');
    }
  }, [activeTab, tabActions, showToast, setIsLoading]);

  // ========== RESET HANDLER ==========
  const handleReset = useCallback(async () => {
    // Prevent multiple reset operations
    if (resetInProgressRef.current) {
      // console.log('🛑 Reset already in progress, preventing duplicate');
      return;
    }

    const tabName = getTabDisplayName(activeTab);
    
    // Different confirmation messages for different tabs
    let confirmMessage, alertTitle;
    
    switch (activeTab) {
      case "add-property-form":
        confirmMessage = __('Are you sure you want to reset all sections and fields? Required tabs (Basic Info) will be preserved.', 'havenlytics');
        alertTitle = __('Reset All Sections?', 'havenlytics');
        break;
      case "property-card-layout":
        confirmMessage = __('Are you sure you want to reset ONLY the', 'havenlytics') + ` ${tabName}? ` + __('This will reset all property card layout settings to defaults.', 'havenlytics');
        alertTitle = __('Reset', 'havenlytics') + ` ${tabName}?`;
        break;
      default:
        confirmMessage = __('Are you sure you want to reset the', 'havenlytics') + ` ${tabName} ` + __('settings?', 'havenlytics');
        alertTitle = __('Reset', 'havenlytics') + ` ${tabName}?`;
    }

    // Use the beautiful HvnlyNabAlert instead of default window.confirm
    const userConfirmed = await window.HvnlyNabAlert.confirm(confirmMessage, alertTitle);
    // console.log(`🔄 RESET CONFIRMED: ${userConfirmed === 'confirm'} for tab: ${activeTab}`);

    if (userConfirmed === 'confirm') {
      // Set reset in progress flag
      resetInProgressRef.current = true;
      setIsResetting(true);
      setIsLoading(true);

      try {
        const currentActions = tabActions[activeTab];
        
        if (currentActions?.resetConfiguration) {
          // console.log('🔗 Calling resetConfiguration for current tab...');
          const success = await currentActions.resetConfiguration();
          // console.log('✅ Reset operation completed with result:', success);
          
          if (!success) {
            // console.log('❌ Reset failed - error toast already shown');
          }
          // Success toast is handled by the tab component, so we don't show another one here
        } else {
          // For tabs without API reset, reset local state
          if (activeTab === "property-card-layout") {
            setPropertyCardLayoutData({
              layout: 'grid',
              columns: 3,
              style: 'modern',
              showPrice: true,
              showAddress: true
            });
          }
          
          // Show success toast ONLY for tabs without API reset
          showToast('success', __('Configuration Reset', 'havenlytics'), `${tabName} ` + __('reset successfully!', 'havenlytics'));
        }
        
      } catch (error) {
        // console.error('❌ Reset operation failed:', error);
        showToast('error', __('Reset Failed', 'havenlytics'), __('Failed to reset configuration:', 'havenlytics') + ` ${error.message}`);
      } finally {
        setIsResetting(false);
        setIsLoading(false);
        // Clear reset in progress flag after a short delay
        setTimeout(() => {
          resetInProgressRef.current = false;
        }, 1000);
        // console.log('🏁 Reset process completed');
      }
    } else {
      // console.log('❌ Reset cancelled by user');
    }
  }, [activeTab, tabActions, showToast, setIsLoading]);

  // Helper function to get display name for toast messages
  const getTabDisplayName = (tabId) => {
    const tab = navigationTabs.find(t => t.id === tabId);
    return tab ? tab.label : __('Configuration', 'havenlytics');
  };

  // Callback to receive actions from tabs
  const handleTabActions = useCallback((tabId, actions) => {
    // console.log('📥 Received actions for tab:', tabId);
    setTabActions(prev => ({
      ...prev,
      [tabId]: actions
    }));
  }, []);

  return (
    <div className="hvnly--builder--int_api__property_builder_tab_navigation">
      {/* ========== STICKY HEADER CLONE ========== */}
      {isSticky && (
        <div className="hvnly--builder--int_api__property_builder_header property-builder-sticky">
          <h1><i className="fas fa-object-group"></i> {__('Property Builder', 'havenlytics')}</h1>
          <div className="hvnly--builder--int_api__property_builder_header_actions">
            <button 
              className="hvnly--builder--int_api__property_builder_btn hvnly--builder--int_api__property_builder_btn_secondary" 
              onClick={handleReset}
              disabled={isResetting || resetInProgressRef.current}
            >
              {isResetting ? (
                <><i className="fas fa-spinner fa-spin"></i> {__('Resetting...', 'havenlytics')}</>
              ) : (
                <><i className="fas fa-sync-alt"></i> {__('Reset', 'havenlytics')}</>
              )}
            </button>
            <button 
              className="hvnly--builder--int_api__property_builder_btn hvnly--builder--int_api__property_builder_btn_primary" 
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <><i className="fas fa-spinner fa-spin"></i> {__('Saving...', 'havenlytics')}</>
              ) : (
                <><i className="fas fa-save"></i> {__('Save Configuration', 'havenlytics')}</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ========== ORIGINAL HEADER ========== */}
      <div className="hvnly--builder--int_api__property_builder_header">
        <h1><i className="fas fa-object-group"></i> {__('Property Builder', 'havenlytics')}</h1>
        <div className="hvnly--builder--int_api__property_builder_header_actions">
          <button 
            className="hvnly--builder--int_api__property_builder_btn hvnly--builder--int_api__property_builder_btn_secondary" 
            onClick={handleReset}
            disabled={isResetting || resetInProgressRef.current}
          >
            {isResetting ? (
              <><i className="fas fa-spinner fa-spin"></i> {__('Resetting...', 'havenlytics')}</>
            ) : (
              <><i className="fas fa-sync-alt"></i> {__('Reset', 'havenlytics')}</>
            )}
          </button>
          <button 
            className="hvnly--builder--int_api__property_builder_btn hvnly--builder--int_api__property_builder_btn_primary" 
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <><i className="fas fa-spinner fa-spin"></i> {__('Saving...', 'havenlytics')}</>
            ) : (
              <><i className="fas fa-save"></i> {__('Save Configuration', 'havenlytics')}</>
            )}
          </button>
        </div>
      </div>

      {/* ========== TAB NAVIGATION ========== */}
      <div className="hvnly--builder--int_api__tab_nav_tabs_main">
        <div 
          className="hvnly--builder--int_api__tab_nav_tabs_wrapper"
          ref={tabsWrapperRef}
        >
          <div className="hvnly--builder--int_api__tab_nav_tabs">
            {navigationTabs.map((tab) => (
              <button
                key={tab.id}
                className={`hvnly--builder--int_api__tab_nav_item ${
                  activeTab === tab.id ? "active" : ""
                }`}
                data-tab={tab.id}
                onClick={() => handleTabClick(tab.id)}
                onMouseEnter={() => setHoverTab(tab.id)}
                onMouseLeave={() => setHoverTab(null)}
              >
                <i className={`fas fa-${tab.icon}`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ========== TAB CONTENT ========== */}
      <div className="hvnly--builder--int_api__tab_nav_content">
        {activeTab === "add-property-form" && (
          <AddPropertyFormTab
            showToast={showToast}
            onActionsReady={(actions) => handleTabActions("add-property-form", actions)}
            isSaving={isSaving}
            isResetting={isResetting}
          />
        )}
        {activeTab === "property-card-layout" && (
          <PropertyCardLayoutTab 
            showToast={showToast}
            setIsLoading={setIsLoading}
            cardData={propertyCardLayoutData}
            onCardChange={setPropertyCardLayoutData}
            onActionsReady={(actions) => handleTabActions("property-card-layout", actions)}
          />
        )}
        {/* Single Property Layout Tab Content - Commented out for future use */}
        {/*
        {activeTab === "single-property-layout" && (
          <SinglePropertyLayoutTab 
            showToast={showToast}
            setIsLoading={setIsLoading}
            layoutData={singlePropertyLayoutData}
            onLayoutChange={setSinglePropertyLayoutData}
            onActionsReady={(actions) => handleTabActions("single-property-layout", actions)}
          />
        )}
        */}
      </div>
    </div>
  );
};

export default TabNavigation;